/**
 * /config/portal/:siteId: a configuração de um site, em cinco abas.
 *
 *   Colunas · Marca e rodapé · Rotas e SEO · Conexão · Conferência
 *
 * A aba aberta vai na URL (`?aba=`), então um link leva direto para ela e o
 * botão voltar do navegador funciona.
 */
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { Button } from '@/components/atoms';
import { AbaColunas } from '@/components/Portal/AbaColunas';
import { AbaConexao } from '@/components/Portal/AbaConexao';
import { AbaConferencia } from '@/components/Portal/AbaConferencia';
import { AbaMarcaRodape } from '@/components/Portal/AbaMarcaRodape';
import { AbaRotasSeo } from '@/components/Portal/AbaRotasSeo';
import { EstadoDoSite } from '@/components/Portal/EstadoDoSite';
import { PortalGate } from '@/components/Portal/PortalGate';
import { useAgora } from '@/hooks/useAgora';
import { usePortalSite } from '@/hooks/usePortal';
import { classificarFalha } from '@/services/portal';
import type { PortalSiteDetalhe } from '@/types/portal';

import styles from '@/components/Portal/Portal.module.css';

const ABAS = [
  { chave: 'colunas', rotulo: 'Colunas' },
  { chave: 'marca', rotulo: 'Marca e rodapé' },
  { chave: 'rotas', rotulo: 'Rotas e SEO' },
  { chave: 'conexao', rotulo: 'Conexão' },
  { chave: 'conferencia', rotulo: 'Conferência' },
] as const;
type ChaveAba = (typeof ABAS)[number]['chave'];

function abaDaUrl(valor: string | null): ChaveAba {
  return ABAS.find((a) => a.chave === valor)?.chave ?? 'colunas';
}

function Conteudo({ detalhe, aba }: { detalhe: PortalSiteDetalhe; aba: ChaveAba }) {
  switch (aba) {
    case 'colunas':
      return <AbaColunas detalhe={detalhe} />;
    case 'marca':
      return <AbaMarcaRodape detalhe={detalhe} />;
    case 'rotas':
      return <AbaRotasSeo detalhe={detalhe} />;
    case 'conexao':
      return <AbaConexao detalhe={detalhe} />;
    case 'conferencia':
      return <AbaConferencia detalhe={detalhe} />;
  }
}

function Site({ siteId }: { siteId: string }) {
  const { data, isLoading, isError, error, refetch } = usePortalSite(siteId);
  const [params, setParams] = useSearchParams();
  const agora = useAgora();
  const aba = abaDaUrl(params.get('aba'));

  if (isLoading) return <p className={styles.mudo}>Carregando o site…</p>;

  if (isError || !data) {
    const tipo = classificarFalha(error);
    if (tipo === 'sem_api') {
      return (
        <div className={styles.avisoWarn} role="alert">
          <span className={styles.avisoTitulo}>Site não encontrado</span>
          <span>
            Este site não está no seu tenant, ou a API do Portal Kit não está instalada nesta
            instância.
          </span>
          <div>
            <Link to="/config/portal">Voltar à lista de sites</Link>
          </div>
        </div>
      );
    }
    if (tipo === 'sem_plano') {
      return (
        <div className={styles.avisoWarn} role="alert">
          <span className={styles.avisoTitulo}>Portal Kit não incluído no plano</span>
        </div>
      );
    }
    return (
      <div className={styles.avisoCrit} role="alert">
        <span className={styles.avisoTitulo}>Não foi possível carregar o site</span>
        <div>
          <Button type="button" size="sm" variant="secondary" onClick={() => void refetch()}>
            Tentar de novo
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.cabecalho}>
        <div>
          <p className={styles.migalhas}>
            <Link to="/config">Configurações</Link> / <Link to="/config/portal">Portal</Link> /{' '}
            {data.nome}
          </p>
          <h1 className={styles.titulo}>{data.nome}</h1>
          <p className={styles.sub}>
            <span className={styles.mono}>{data.url}</span>
          </p>
        </div>
        <EstadoDoSite site={data} agora={agora} />
      </div>

      <div className={styles.abas} role="tablist" aria-label="Configuração do site">
        {ABAS.map((a) => (
          <button
            key={a.chave}
            type="button"
            role="tab"
            id={`aba-portal-${a.chave}`}
            aria-selected={aba === a.chave}
            aria-controls="painel-portal"
            className={aba === a.chave ? styles.abaAtiva : styles.aba}
            onClick={() => setParams({ aba: a.chave }, { replace: true })}
          >
            {a.rotulo}
          </button>
        ))}
      </div>

      <div id="painel-portal" role="tabpanel" aria-labelledby={`aba-portal-${aba}`}>
        <Conteudo detalhe={data} aba={aba} />
      </div>
    </>
  );
}

export function ConfigPortalSite() {
  const { siteId } = useParams<{ siteId: string }>();
  return (
    <div className={styles.pagina}>
      <PortalGate>{siteId ? <Site siteId={siteId} /> : null}</PortalGate>
    </div>
  );
}
